import { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { checkIn, checkOut, getTodayStatus, toggleBreak } from "../services/attendanceservice";
import DSRModal from "../dsr/dsr_modal";
import { useAuth } from "../context/authcontext";
import { AttendanceStatus } from "../../constants/constants";

// India Standard Time is a fixed UTC+5:30 offset (no DST), so it can be
// hard-coded rather than relying on the Intl timezone database.
const IST_TIMEZONE = "Asia/Kolkata";
const IST_OFFSET_MINUTES = 5 * 60 + 30;

export default function CheckInOut({ onAttendanceChange }) {
  const { hasPermission } = useAuth();
  const [checkInTime, setCheckInTime]   = useState(null);
  const [checkOutTime, setCheckOutTime] = useState(null);
  const [liveTime, setLiveTime]         = useState(new Date());
  const [checkInAnim, setCheckInAnim]   = useState(false);
  const [checkOutAnim, setCheckOutAnim] = useState(false);
  const [loading, setLoading]           = useState(false);
  const [attendanceId, setAttendanceId] = useState(null);
  const [onBreak, setOnBreak]           = useState(false);
  const [breakStartTime, setBreakStartTime] = useState(null);
  const [breakHours, setBreakHours]     = useState(0);
  const [workMode, setWorkMode]         = useState(AttendanceStatus.PRESENT);
  const [showDSRModal, setShowDSRModal] = useState(false);

  /* Live clock */
  useEffect(() => {
    const t = setInterval(() => setLiveTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // Parses a time value coming from the backend into the correct absolute
  // Date instant, treating it as IST regardless of what timezone the
  // browser/OS is actually running in:
  //  - Full ISO strings ("...T...") already carry their own offset/zone,
  //    so they're trusted as-is — `new Date()` resolves them correctly no
  //    matter the local machine's timezone.
  //  - Bare "HH:MM:SS" strings have no date or zone info. These are treated
  //    as an IST wall-clock time for "today in IST", and converted into the
  //    actual UTC instant they represent. This keeps the underlying Date
  //    object correct (and safe to compare/diff) even if the browser's
  //    system clock is set to a different timezone than IST.
  const parseTime = (timeStr) => {
    if (!timeStr) return null;
    if (timeStr.includes("T")) {
      return new Date(timeStr);
    }

    const [hh, mm, ss] = timeStr.split(":").map((p) => parseInt(p, 10) || 0);

    // "Today" as seen from IST, not as seen from the local system clock —
    // matters near midnight if the two are on different calendar dates.
    const istNow = new Date(Date.now() + IST_OFFSET_MINUTES * 60000);
    const y = istNow.getUTCFullYear();
    const m = istNow.getUTCMonth();
    const d = istNow.getUTCDate();

    const utcMs = Date.UTC(y, m, d, hh, mm, ss) - IST_OFFSET_MINUTES * 60000;
    return new Date(utcMs);
  };

  // All clock-face displays are explicitly pinned to IST via timeZone, so
  // they show the same time regardless of the viewing machine's own
  // timezone setting — this is what makes it "IST, not system time".
  const fmtShort = (date) =>
    date
      ? date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", timeZone: IST_TIMEZONE })
      : "--:--";

  const fmtDate = (date) =>
    date
      ? date.toLocaleDateString([], {
          weekday: "short",
          day: "numeric",
          month: "short",
          year: "numeric",
          timeZone: IST_TIMEZONE,
        })
      : null;

  /* Load today's status */
  useEffect(() => {
    const loadStatus = async () => {
      try {
        const res = await getTodayStatus();
        const data = res.data || res;
        if (data?.check_in_time) {
          setCheckInTime(parseTime(data.check_in_time));
          setAttendanceId(data.id);
        }
        if (data?.check_out_time) {
          setCheckOutTime(parseTime(data.check_out_time));
        }
        if (data?.current_break_start && !data?.check_out_time) {
          setOnBreak(true);
          setBreakStartTime(parseTime(data.current_break_start));
        }
        if (data?.total_break_hours) setBreakHours(parseFloat(data.total_break_hours));
      } catch {
        /* No record yet — that's fine */
      }
    };
    loadStatus();
  }, []);

  // Session duration (Check-in card, "Duration" / "Total Hours") — back to
  // a live HH:MM:SS stopwatch format.
  const elapsed = () => {
    if (!checkInTime) return null;
    const end  = checkOutTime || liveTime;
    const diff = Math.max(0, Math.floor((end - checkInTime) / 1000));
    const h    = Math.floor(diff / 3600).toString().padStart(2, "0");
    const m    = Math.floor((diff % 3600) / 60).toString().padStart(2, "0");
    const s    = Math.floor(diff % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // Live break timer (shown on the full-screen break overlay) — also stays
  // HH:MM:SS, same as session duration.
  const breakElapsed = () => {
    if (!breakStartTime) return "00:00:00";
    const diff = Math.max(0, Math.floor((liveTime - breakStartTime) / 1000));
    const h    = Math.floor(diff / 3600).toString().padStart(2, "0");
    const m    = Math.floor((diff % 3600) / 60).toString().padStart(2, "0");
    const s    = Math.floor(diff % 60).toString().padStart(2, "0");
    return `${h}:${m}:${s}`;
  };

  // The "Break" summary field is a total pulled from the backend as decimal
  // hours (e.g. 0.02), not a live stopwatch — displayed as "Xh Ym" instead
  // of the raw decimal so it reads naturally (e.g. "0h 1m" instead of
  // "0.02h").
  const fmtHoursAsHM = (hoursFloat) => {
    const totalMinutes = Math.max(0, Math.round((hoursFloat || 0) * 60));
    const h = Math.floor(totalMinutes / 60);
    const m = totalMinutes % 60;
    return `${h}h ${m}m`;
  };

  // Short, human-friendly duration for the "Break ended" toast, computed
  // from the actual elapsed milliseconds rather than the backend's raw
  // decimal-hours message (which reads oddly for short breaks, e.g.
  // "Duration added: 0.0 hours" for a 30-second break). Picks the coarsest
  // sensible unit: "1h 5m" for long breaks, "2m" for short ones, and falls
  // back to seconds ("45s") for breaks under a minute so it never just
  // says "0m".
  const fmtDurationShort = (ms) => {
    const totalSeconds = Math.max(0, Math.round(ms / 1000));
    const h = Math.floor(totalSeconds / 3600);
    const m = Math.floor((totalSeconds % 3600) / 60);
    const s = totalSeconds % 60;
    if (h > 0) return `${h} h ${m} m`;
    if (m > 0) return `${m} m`;
    return `${s} s`;
  };

  const handleCheckIn = async () => {
    if (checkInTime || loading) return;
    setLoading(true);
    try {
      const res = await checkIn({ status: workMode });
      const data = res.data || res;
      setAttendanceId(data.id);
      setCheckInTime(new Date());
      setCheckInAnim(true);
      setTimeout(() => setCheckInAnim(false), 700);
      // All success toasts are styled globally (premium green) via the
      // shared .Toastify__toast--success rules — no custom className needed.
      toast.success("Checked in successfully");
      onAttendanceChange?.(); // let Dashboard refetch attendance_summary
    } catch (err) {
      toast.error(err.response?.data?.message || "Check-in failed");
    } finally {
      setLoading(false);
    }
  };

  const openDSRModal = () => {
    if (!checkInTime || checkOutTime || loading) return;
    if (hasPermission("dsr.create")) {
      setShowDSRModal(true);
    } else {
      handleCheckOut();
    }
  };

  const handleCheckOut = async () => {
    if (!checkInTime || checkOutTime || loading) return;
    setLoading(true);
    try {
      await checkOut({ attendance_id: attendanceId });
      setCheckOutTime(new Date());
      setCheckOutAnim(true);
      setTimeout(() => setCheckOutAnim(false), 700);
      setShowDSRModal(false);
      // Everything else uses the shared green success styling; this one
      // gets the "toast-checkout" class to flip it red (see toast.css).
      toast.success("Checked out successfully", { className: "toast-checkout" });
      onAttendanceChange?.(); // let Dashboard refetch attendance_summary
    } catch (err) {
      toast.error(err.response?.data?.message || "Check-out failed");
    } finally {
      setLoading(false);
    }
  };

  const handleToggleBreak = async () => {
    if (loading) return;
    setLoading(true);
    try {
      const res = await toggleBreak();
      const data = res.data || res;
      if (!onBreak) {
        const startedAt = new Date();
        setBreakStartTime(startedAt);
        toast.success(`Break started at ${fmtShort(startedAt)}.`);
      } else {
        // Compute the just-finished break's duration client-side from the
        // actual start time, instead of trusting the backend's decimal-hours
        // message, so it reads as "2m" / "1h 5m" instead of "0.03 hours".
        const durationMs = breakStartTime ? Date.now() - breakStartTime.getTime() : 0;
        setBreakStartTime(null);
        toast.success(`Break ended. Duration added: ${fmtDurationShort(durationMs)}`);
      }
      setOnBreak(!onBreak);
      if (data?.total_break_hours) setBreakHours(parseFloat(data.total_break_hours));
      onAttendanceChange?.(); // break hours can feed into the summary too
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to toggle break");
    } finally {
      setLoading(false);
    }
  };

  const status = !checkInTime ? "idle" : !checkOutTime ? "active" : "done";

  return (
    <div className="cio-root">
      <div className="cio-live">
        <span className="cio-live-date">
          {liveTime.toLocaleDateString([], {
            weekday: "long",
            day: "numeric",
            month: "long",
            year: "numeric",
            timeZone: IST_TIMEZONE,
          })}
        </span>
        <span className="cio-live-clock">
          {liveTime.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            timeZone: IST_TIMEZONE,
          })}
        </span>
      </div>

      <div className={`cio-status-pill cio-status-${status}`}>
        <span className="cio-status-dot" />
        {status === "idle"   && "Not Checked In"}
        {status === "active" && "Session Active"}
        {status === "done"   && "Session Completed"}
      </div>

      <div className="cio-cards">
        {/* CHECK-IN */}
        <div className={`cio-card cio-card-in ${checkInTime ? "cio-card-done" : ""} ${checkInAnim ? "cio-card-pulse" : ""}`}>
          <div className="cio-card-top">
            <div className="cio-icon-wrap cio-icon-in">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><polyline points="12 8 12 12 14 14"/>
              </svg>
            </div>
            <span className="cio-card-label">CHECK-IN</span>
          </div>
          <div className="cio-time-display">
            {checkInTime ? (
              <>
                <span className="cio-time-main">{fmtShort(checkInTime)}</span>
                <span className="cio-time-date">{fmtDate(checkInTime)}</span>
              </>
            ) : (
              <span className="cio-time-empty">-- : --</span>
            )}
          </div>
          {!checkInTime ? (
            <div style={{ display: "flex", flexDirection: "column", gap: "8px", width: "100%" }}>
              <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginBottom: "4px" }}>
              <button
  type="button"
  onClick={() => setWorkMode(AttendanceStatus.PRESENT)}
  style={{
    padding: "6px 14px",
    borderRadius: "20px",
    border:
      workMode === AttendanceStatus.PRESENT
        ? "1px solid #BFDBFE"
        : "1px solid #D1D5DB",
    background:
      workMode === AttendanceStatus.PRESENT
        ? "#EEF4FF"
        : "#FFFFFF",
    color:
      workMode === AttendanceStatus.PRESENT
        ? "#2563EB"
        : "#6B7280",
    fontSize: "0.8rem",
    fontWeight: workMode === AttendanceStatus.PRESENT ? "700" : "600",
    cursor: "pointer",
    transition: "all 0.25s ease",
    boxShadow:
      workMode === AttendanceStatus.PRESENT
        ? "0 2px 8px rgba(37,99,235,0.12)"
        : "none",
  }}
>
  🏢 Office
</button>
              <button
  type="button"
  onClick={() => setWorkMode(AttendanceStatus.WORK_FROM_HOME)}
  style={{
    padding: "6px 14px",
    borderRadius: "20px",
    border:
      workMode === AttendanceStatus.WORK_FROM_HOME
        ? "1px solid #BFDBFE"
        : "1px solid #D1D5DB",
    background:
      workMode === AttendanceStatus.WORK_FROM_HOME
        ? "#EEF4FF"
        : "#FFFFFF",
    color:
      workMode === AttendanceStatus.WORK_FROM_HOME
        ? "#2563EB"
        : "#6B7280",
    fontSize: "0.8rem",
    fontWeight: workMode === AttendanceStatus.WORK_FROM_HOME ? "700" : "600",
    cursor: "pointer",
    transition: "all 0.25s ease",
    boxShadow:
      workMode === AttendanceStatus.WORK_FROM_HOME
        ? "0 2px 8px rgba(37,99,235,0.12)"
        : "none",
  }}
>
  🏠 WFH
</button>
              </div>
              <button className="cio-btn cio-btn-in" onClick={handleCheckIn} disabled={loading}>
                <span className="cio-btn-ring" />
                {loading ? "..." : `Check In (${workMode === AttendanceStatus.WORK_FROM_HOME ? "WFH" : "Office"})`}
              </button>
            </div>
          ) : (
            <div className="cio-confirmed">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Confirmed
            </div>
          )}
        </div>

        <div className="cio-middle">
          {checkInTime && (
            <>
              <div className="cio-elapsed-label">Duration</div>
              <div className={`cio-elapsed ${!checkOutTime ? "cio-elapsed-live" : ""}`}>{elapsed()}</div>
              {!checkOutTime && <div className="cio-elapsed-sub">ongoing</div>}
            </>
          )}
          {!checkInTime && <div className="cio-middle-dash">—</div>}

          {/* Break Button */}
          {checkInTime && !checkOutTime && (
            <button
              className={`cio-btn ${onBreak ? "cio-btn-out" : "cio-btn-in"}`}
              style={{ marginTop: 10, fontSize: "0.85rem", padding: "8px 20px" }}
              onClick={handleToggleBreak}
              disabled={loading}
            >
              <span className="cio-btn-ring" />
              {loading ? "..." : onBreak ? "☕ End Break" : "☕ Take Break"}
            </button>
          )}
        </div>

        {/* CHECK-OUT */}
        <div className={`cio-card cio-card-out ${checkOutTime ? "cio-card-done" : ""} ${!checkInTime ? "cio-card-locked" : ""} ${checkOutAnim ? "cio-card-pulse" : ""}`}>
          <div className="cio-card-top">
            <div className="cio-icon-wrap cio-icon-out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                <polyline points="16 17 21 12 16 7"/>
                <line x1="21" y1="12" x2="9" y2="12"/>
              </svg>
            </div>
            <span className="cio-card-label">CHECK-OUT</span>
          </div>
          <div className="cio-time-display">
            {checkOutTime ? (
              <>
                <span className="cio-time-main">{fmtShort(checkOutTime)}</span>
                <span className="cio-time-date">{fmtDate(checkOutTime)}</span>
              </>
            ) : (
              <span className="cio-time-empty">-- : --</span>
            )}
          </div>
          {!checkOutTime ? (
            <button
              className={`cio-btn cio-btn-out ${!checkInTime ? "cio-btn-disabled" : ""}`}
              onClick={openDSRModal}
              disabled={!checkInTime || loading}
            >
              <span className="cio-btn-ring" />
              {loading ? "..." : "Check Out"}
            </button>
          ) : (
            <div className="cio-confirmed cio-confirmed-out">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Confirmed
            </div>
          )}
        </div>
      </div>

      {checkOutTime && (
        <div className="cio-summary">
          <div className="cio-summary-item">
            <span className="cio-summary-label">Total Hours</span>
            <span className="cio-summary-value">{elapsed()}</span>
          </div>
          <div className="cio-summary-sep" />
          <div className="cio-summary-item">
            <span className="cio-summary-label">In</span>
            <span className="cio-summary-value">{fmtShort(checkInTime)}</span>
          </div>
          <div className="cio-summary-sep" />
          <div className="cio-summary-item">
            <span className="cio-summary-label">Out</span>
            <span className="cio-summary-value">{fmtShort(checkOutTime)}</span>
          </div>
          {breakHours > 0 && (
            <>
              <div className="cio-summary-sep" />
              <div className="cio-summary-item">
                <span className="cio-summary-label">Break</span>
                <span className="cio-summary-value">{fmtHoursAsHM(breakHours)}</span>
              </div>
            </>
          )}
        </div>
      )}

      {!checkOutTime && onBreak && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
          background: 'rgba(15, 23, 42, 0.85)', zIndex: 9999,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          color: '#fff', backdropFilter: 'blur(24px)', WebkitBackdropFilter: 'blur(24px)'
        }}>
          <div style={{ fontSize: '5rem', marginBottom: '1.5rem', filter: 'drop-shadow(0 0 20px rgba(255,255,255,0.2))' }}>☕</div>
          <h1 style={{ fontSize: '2.5rem', marginBottom: '1rem', fontWeight: 600, letterSpacing: '-0.02em', textShadow: '0 4px 20px rgba(0,0,0,0.4)' }}>You are on a Break</h1>
          <div style={{ 
            fontSize: '5rem', fontWeight: 'bold', fontFamily: "'JetBrains Mono', monospace", 
            marginBottom: '3rem', letterSpacing: '-0.03em', 
            background: 'linear-gradient(135deg, #6ee7b7, #3b82f6)',
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent',
            filter: 'drop-shadow(0 4px 24px rgba(59, 130, 246, 0.3))'
          }}>
            {breakElapsed()}
          </div>
          <button
            className="cio-btn cio-btn-out"
            style={{ 
              fontSize: "1.1rem", padding: "18px 40px", borderRadius: "100px", 
              boxShadow: '0 8px 32px rgba(99, 102, 241, 0.4)',
              background: 'linear-gradient(135deg, #3b82f6, #1a56db)',
              border: '1px solid rgba(255,255,255,0.2)'
            }}
            onClick={handleToggleBreak}
            disabled={loading}
          >
            <span className="cio-btn-ring" style={{ borderRadius: '100px' }} />
            {loading ? "..." : "End Break"}
          </button>
        </div>
      )}

      {showDSRModal && (
        <DSRModal 
          onClose={() => setShowDSRModal(false)} 
          onCheckOutSuccess={handleCheckOut} 
        />
      )}
    </div>
  );
}